import * as React from "react";

import { Block } from "baseui/block";
import {
  ProgressBar as BaseProgressBar,
  ProgressBarOverrides,
} from "baseui/progress-bar";
import { mergeOverrides } from "baseui/helpers/overrides";

import { filterChildren } from "../utils/components";

/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
// TODO: Enable the proptype check next time when this file is changed

export type ProgressBarProps = {
  current?: number;
  total?: number;
  label?: string;
  active?: boolean;
  complete?: boolean;
  overrides?: ProgressBarOverrides;
};

/**
 * Displays progress through a single step
 * To quickly display a started step with 0 progress, use 'active'
 * To quickly display a completed step, use 'complete'
 */
function ProgressBar({
  current,
  total,
  label,
  active,
  complete,
  overrides = {},
}: ProgressBarProps) {
  return (
    <BaseProgressBar
      value={getProgressValue({ current, total, complete })}
      getProgressLabel={() => label}
      showLabel={!!label}
      overrides={mergeOverrides(
        {
          BarContainer: {
            style: ({ $theme }) => ({
              marginLeft: 0,
              marginRight: 0,
              marginBottom: $theme.sizing.scale100,
            }),
          },
          BarProgress: {
            style: ({ $theme }) => ({
              backgroundColor: $theme.colors.contentPrimary,
            }),
          },
          Bar: {
            style: ({ $theme }) => ({
              height: $theme.sizing.scale100,
              backgroundColor: $theme.colors.contentInverseTertiary,
            }),
          },
          Label: {
            style: ({ $theme }) => ({
              color:
                current || complete || active
                  ? $theme.colors.contentPrimary
                  : $theme.colors.contentSecondary,
              textAlign: "left",
              textTransform: "uppercase",
              [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                display: "none",
              },
            }),
          },
        },
        overrides
      )}
    />
  );
}

/**
 * Used to render progress through a multi step process
 */
ProgressBar.Container = function ({ children }) {
  const getSubProgressOverrides = ({ active, current, total }: ProgressBarProps) => ({
    Root: {
      style: ({ $theme }) => ({
        marginLeft: $theme.sizing.scale100,
        marginRight: $theme.sizing.scale100,
        [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
          display: isActive({
            active,
            current,
            total,
          })
            ? "block"
            : "none",
          marginLeft: 0,
          marginRight: 0,
        },
      }),
    },
  });
  const progressBars = filterChildren(children, ProgressBar).map(({ props }) => {
    const overrides = mergeOverrides(getSubProgressOverrides(props), props.overrides);
    return (
      <ProgressBar
        {...props}
        overrides={overrides}
        key={`sub-progress-${props.label}-${props.current}-${props.total}`}
      />
    );
  });
  return <Block display="flex">{progressBars}</Block>;
};

/**
 * Calculates what value to pass to baseweb's progress bar
 * based on its completion (specified with complete) or
 * with the progress (current) through the steps available (total)
 * This should be a value from 0-100, where 100 is complete
 */
function getProgressValue({
  current,
  total,
  complete,
}: {
  current: number;
  total: number;
  complete: boolean;
}) {
  if (complete) {
    return 100;
  }
  return total ? (100 * current) / total : 0;
}

/**
 * A progress bar is active if it is specifically active,
 * or has a current less than its total
 */
function isActive({
  current,
  total,
  active,
}: {
  current: number;
  total: number;
  active: boolean;
}) {
  if (active) {
    return true;
  }
  return current && total && current < total;
}

export { ProgressBar };
